/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, no-restricted-properties, warp-drive/no-legacy-request-patterns */
// Extends From
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { action, computed } from '@ember/object';
import { setDiff, mapBy, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import config from 'linced-admin/config/environment';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import {
  compact,
  mapBy as mapByArray,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { minimalTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { TrackedArray } from 'tracked-built-ins';

export default class TemplateController extends Controller {
  // Services
  @service
  paperToaster;

  @service
  session;

  @service
  store;

  PersonType = PersonType;

  // Attrs
  editorOptions = Object.freeze(minimalTinyMCEConfig());

  @alias('model.schema')
  schema;

  @alias('schema.hasDirtyAttributes')
  recordIsDirty;

  // State
  @tracked
  deleteSectionDialogOpen = false;

  deleteInProgress = false;

  @tracked
  expandedSections = [];

  enrolmentIntroductionDialogOpen = false;

  @tracked
  enrolmentIntroduction = '';

  @action
  setEnrolmentIntroduction(value) {
    this.enrolmentIntroduction = value;
  }

  isSortable = false;

  @tracked
  sectionTitle = '';

  @action
  setSectionTitle(value) {
    this.sectionTitle = value;
  }

  @tracked
  sectionDescription = '';

  @action
  setSectionDescription(value) {
    this.sectionDescription = value;
  }

  @tracked
  sectionDialogOpen = false;

  saveInProgress = false;

  @computed('model.fieldSets.[]')
  get filteredFieldSets() {
    return this.model.fieldSets.filter((set) => {
      return [
        PersonType.LEARNER,
        PersonType.CAREGIVER,
        PersonType.DOCTORS_AND_SPECIALISTS,
      ].includes(set.personType);
    });
  }

  @tracked
  selectedFieldSets = [];

  @setDiff('filteredFieldSets', 'selectedFieldSets')
  remainingFieldSets;

  @computed(
    'schema.{sections.[],primaryContacts,emergencyContacts,medicalContacts,medicalConditions}',
  )
  get allSections() {
    return [
      ...this.schema.sections,
      ...compact([
        this.schema.primaryContacts,
        this.schema.emergencyContacts,
        this.schema.medicalContacts,
        this.schema.medicalConditions,
      ]),
    ];
  }

  // TrackedArray, not a plain array: this feeds a <DragSortList @items>
  // whose sortDragEnd mutates it in place via native .splice() (see below).
  // It stays a classic @computed (not a plain getter) because its cache
  // *is* the reordered state - dateSelected-style, a plain getter would
  // rebuild fresh from allSections on every read and discard the reorder.
  @computed('allSections.[]')
  get sortedSections() {
    return new TrackedArray(sortBy(this.allSections, 'index', 'title'));
  }

  @computed('session.school.jurisdictionalId')
  get enrolmentUrl() {
    let jurisdictionalId = this.get('session.school.jurisdictionalId');
    return `${config.APP.enrolmentHost}/apply/NZ/${jurisdictionalId}?preview=${this.schema.id}`;
  }

  @computed('model.{allFields.[],fieldGroups.[]}')
  get allLearnerFields() {
    return this.model.allFields.filter((field) => {
      let personType = field.get('personType');
      if (!personType) {
        personType = field.get('templateGroupId.personType');
      }
      // Include unknowns, so that we don't completely lose them.  There shouldn't be (m)any.
      return personType === PersonType.LEARNER || !personType;
    });
  }

  @computed('allLearnerFields.[]', 'selectedFieldSets.[]')
  get filteredLearnerFields() {
    // If no filter defined, return all learner fields.
    if (isEmpty(this.selectedFieldSets)) {
      return this.allLearnerFields;
    }

    // If filter is defined, return only learner fields from the selected template set(s).
    let setIds = mapByArray(this.selectedFieldSets, 'id');
    return this.allLearnerFields.filter((field) => {
      let setId = field.get('templateSetId.id');
      return setIds.includes(setId);
    });
  }

  @mapBy('schema.sections', 'fieldIds')
  selectedLearnerFieldIds;

  // NOTE: selectedLearnerFields may contain fields which don't match the current filter; use the full list of fields.
  @computed('allLearnerFields.[]', 'selectedLearnerFieldIds.[]')
  get selectedLearnerFields() {
    let fields = this.allLearnerFields;
    let selectedIds = this.get('schema.sections').reduce(function (
      ids,
      section,
    ) {
      return [...ids, ...mapByArray(section.get('fields'), 'fieldId')];
    }, []);
    return fields.filter(function (field) {
      return selectedIds.includes(field.get('id'));
    });
  }

  @setDiff('filteredLearnerFields', 'selectedLearnerFields')
  availableLearnerFields;

  @computed('model.{allFields.[],fieldGroups.[]}')
  get allCaregiverFields() {
    return this.model.allFields.filter((field) => {
      let personType = field.get('personType');
      if (!personType) {
        personType = field.get('templateGroupId.personType');
      }
      // Include unknowns, so that we don't completely lose them.  There shouldn't be (m)any.
      return personType === PersonType.CAREGIVER || !personType;
    });
  }

  @computed('allCaregiverFields.[]', 'selectedFieldSets.[]')
  get filteredCaregiverFields() {
    // If no filter defined, return all caregiver fields.
    if (isEmpty(this.selectedFieldSets)) {
      return this.allCaregiverFields;
    }

    // If filter is defined, return only caregiver fields from the selected template set(s).
    let setIds = mapByArray(this.selectedFieldSets, 'id');
    return this.allCaregiverFields.filter((field) => {
      let setId = field.get('templateSetId.id');
      return setIds.includes(setId);
    });
  }

  // NOTE: selected*ContactFields may contain fields which don't match the current filter; use the full list of fields.
  @computed('allCaregiverFields.[]', 'schema.primaryContacts.fields.[]')
  get selectedPrimaryContactFields() {
    let fields = this.allCaregiverFields;
    let selectedIds = mapByArray(this.schema.primaryContacts.fields, 'fieldId');
    return fields.filter(function (field) {
      return selectedIds.includes(field.get('id'));
    });
  }

  @setDiff('filteredCaregiverFields', 'selectedPrimaryContactFields')
  availablePrimaryContactFields;

  @computed('allCaregiverFields.[]', 'schema.emergencyContacts.fields.[]')
  get selectedEmergencyContactFields() {
    let fields = this.allCaregiverFields;
    let selectedIds = mapByArray(
      this.schema.emergencyContacts.fields,
      'fieldId',
    );
    return fields.filter(function (field) {
      return selectedIds.includes(field.get('id'));
    });
  }

  @setDiff('filteredCaregiverFields', 'selectedEmergencyContactFields')
  availableEmergencyContactFields;

  @computed('model.{allFields.[],fieldGroups.[]}')
  get allMedicalContactFields() {
    return this.model.allFields.filter((field) => {
      let personType = field.get('personType');
      if (!personType) {
        personType = field.get('templateGroupId.personType');
      }
      // Include unknowns, so that we don't completely lose them.  There shouldn't be (m)any.
      return personType === PersonType.DOCTORS_AND_SPECIALISTS || !personType;
    });
  }

  @computed('allMedicalContactFields.[]', 'selectedFieldSets.[]')
  get filteredMedicalContactFields() {
    // If no filter defined, return all medical contact fields.
    if (isEmpty(this.selectedFieldSets)) {
      return this.allMedicalContactFields;
    }

    // If filter is defined, return only medical contact fields from the
    // selected template set(s).
    let setIds = mapByArray(this.selectedFieldSets, 'id');
    return this.allMedicalContactFields.filter((field) => {
      let setId = field.get('templateSetId.id');
      return setIds.includes(setId);
    });
  }

  @computed('allMedicalContactFields.[]', 'schema.medicalContacts.fieldIds')
  get selectedMedicalContactFields() {
    let fields = this.allMedicalContactFields;
    let selectedIds = mapByArray(this.schema.medicalContacts.fields, 'fieldId');
    return fields.filter(function (field) {
      return selectedIds.includes(field.get('id'));
    });
  }

  @setDiff('filteredMedicalContactFields', 'selectedMedicalContactFields')
  availableMedicalContactFields;

  @tracked
  section;

  @tracked
  sectionToDelete;

  @tracked
  selectedField;

  @action
  toggleFieldSet(set) {
    if (this.selectedFieldSets.includes(set)) {
      this.selectedFieldSets = this.selectedFieldSets.filter((s) => s !== set);
    } else {
      this.selectedFieldSets = [...this.selectedFieldSets, set];
    }
  }

  @action
  toggleSection(section) {
    if (this.expandedSections.includes(section)) {
      this.expandedSections = this.expandedSections.filter(
        (s) => s !== section,
      );
    } else {
      this.expandedSections = [...this.expandedSections, section];
    }
  }

  // isContactSection receives an extra parameter when called by the template in order to trigger
  // the fieldIds computed property on all the sections to be calculated.  Otherwise, it's deemed unused and thus
  // won't be computed at all - resulting in all fields always being available.
  @action
  isContactSection(section) {
    switch (section) {
      case this.schema.primaryContacts:
      case this.schema.emergencyContacts:
        return true;
      default:
        return false;
    }
  }

  @action
  isMedicalContactSection(section) {
    switch (section) {
      case this.schema.medicalContacts:
        return true;
      default:
        return false;
    }
  }

  @action
  isConditionSection(section) {
    switch (section) {
      case this.schema.medicalConditions:
        return true;
      default:
        return false;
    }
  }

  @action
  addField(section, field) {
    let props = field.getProperties(
      'isSiblingLinked',
      'key',
      'inputType',
      'dataType',
      'label',
      'default',
      'placeholder',
      'htmlAttributes',
      'isRequired',
      'optionsFromEnum',
    );
    // `options` is a fragment, so needs to be copied into the new field fragment.
    if (!isEmpty(field.options)) {
      props.options = field.options.copy();
    }
    // `default` is also a fragment, so needs to be copied into the new field fragment too.
    if (!isEmpty(field.default)) {
      props.default = field.default.copy();
    }
    props.fieldId = field.get('id');
    props.index = section.get('fields.length') + 1;
    section
      .get('fields')
      .pushObject(this.store.createFragment('enrolments/field', props));
    this.selectedField = null;
  }

  @action
  removeField(section, field) {
    section.get('fields').removeObject(field);
  }

  @action
  toggleSectionDialog() {
    this.toggleProperty('sectionDialogOpen');
    if (!this.sectionDialogOpen) {
      this.resetSectionForm();
    }
  }

  @action
  editSection(section) {
    this.section = section;
    this.sectionTitle = section.get('title');
    this.sectionDescription = section.get('description');

    this.sectionDialogOpen = true;
  }

  @action
  saveSection() {
    let section = this.section;

    if (isEmpty(section)) {
      // Creating a new section
      let section = this.get('schema.sections').createFragment({
        title: this.sectionTitle,
        description: this.sectionDescription,
        index: this.get('schema.sections.length') + 1,
      });

      // Expand the new section by default, so that fields can be added.
      this.expandedSections = [...this.expandedSections, section];
    } else {
      // Editing and existing section
      section.set('title', this.sectionTitle);
      section.set('description', this.sectionDescription);
    }

    this.sectionDialogOpen = false;
    this.resetSectionForm();
  }

  @action
  deleteSection(section) {
    this.sectionToDelete = section;
    this.deleteSectionDialogOpen = true;
  }

  @action
  confirmDeleteSection(confirmed) {
    if (confirmed) {
      this.get('schema.sections').removeObject(this.sectionToDelete);
    }

    // Tidy up / close dialog.
    this.deleteSectionDialogOpen = false;
    this.sectionToDelete = null;
  }

  @action
  saveSchema() {
    let schema = this.schema;
    schema.save().then(() => {
      this.paperToaster.show('Enrolment form template saved successfully.', {
        duration: 4000,
        position: 'top right',
        toastClass: 'success',
      });
    });
  }

  // drag-drop sorting
  //
  // Shared by two DragSortList groups that never mix (different @group
  // names, so ember-drag-sort never lets one drag into the other):
  // "sections" over `sortedSections` (a TrackedArray) and "fields" over
  // Enrolment::FormSection's `section.fields` (a genuine @fragmentArray, a
  // FragmentArray/StatefulArray). Both get a working `.splice()` now - the
  // fragment-array-mutable initializer patches one onto StatefulArray, since
  // it only ever had `.replace()` (which `removeAt`/`insertAt` route through)
  // natively.
  @action
  sortDragEnd({
    draggedItem,
    sourceList,
    sourceIndex,
    targetList,
    targetIndex,
  }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    sourceList.splice(sourceIndex, 1);
    if (sourceList === targetList) {
      targetList.splice(targetIndex, 0, draggedItem);
    } else {
      // Fields are fragments, so if the list changes they must be copied
      // as fragments' owners can't be changed by simply moving the fragment over.
      targetList.splice(targetIndex, 0, draggedItem.copy());
    }

    targetList.forEach((entity, index) => {
      entity.set('index', index + 1);
    });
  }

  @action
  toggleEnrolmentIntroductionDialog() {
    this.enrolmentIntroduction = this.schema.introduction;
    this.toggleProperty('enrolmentIntroductionDialogOpen');
  }

  @action
  saveEnrolmentDescription() {
    this.toggleProperty('enrolmentIntroductionDialogOpen');
    this.set('schema.introduction', this.enrolmentIntroduction);
    this.enrolmentIntroduction = '';
  }

  resetSectionForm() {
    this.section = '';
    this.sectionTitle = '';
    this.sectionDescription = '';
  }

  reset() {
    this.selectedFieldSets = [];
    this.expandedSections = [];
  }
}
